import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n, LangToggle } from "../../i18n";
import { getMyUsername, setMyUsername } from "../../api/collab";
import { ApiError } from "../../api/client";
import { BillingModal } from "../BillingModal";
import { CronModal } from "../CronModal";
import { AdminScreen } from "../AdminScreen";
import { Avatar } from "./avatar";

type Section = "account" | "language" | "billing" | "cron" | "admin";

/**
 * Settings surface — a full page (rail + section sidebar + content) instead of a
 * pop-out drawer. Keeps the existing functionality: profile/sign-out, language,
 * billing, scheduled tasks, and admin. Billing/Cron reuse their modal components
 * rendered inline (chrome neutralized by `.settings-embed`).
 */
export function SettingsScreen() {
  const { user, isAdmin, client, signOut } = useAuth();
  const { t } = useI18n();
  const [section, setSection] = useState<Section>("account");
  // Mobile only: whether a section's content (detail) is shown vs. the section
  // list. Ignored on desktop (both panes always visible).
  const [entered, setEntered] = useState(false);
  const noop = () => {};

  // @username (optional, globally unique, min 3 chars).
  const [username, setUsername] = useState("");
  const [savedUsername, setSavedUsername] = useState<string | null>(null);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    void getMyUsername(client)
      .then(({ username: u }) => {
        if (cancelled) return;
        setSavedUsername(u);
        setUsername(u ?? "");
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [client]);

  const saveUsername = useCallback(async () => {
    if (!client) return;
    const u = username.trim();
    setNameError(null);
    setNameSaved(false);
    if (u.length < 3) {
      setNameError(t("settings.username.invalid"));
      return;
    }
    setSavingName(true);
    try {
      const { username: saved } = await setMyUsername(client, u);
      setSavedUsername(saved);
      setUsername(saved);
      setNameSaved(true);
    } catch (err) {
      setNameError(err instanceof ApiError && err.status === 409 ? t("settings.username.taken") : t("settings.username.invalid"));
    } finally {
      setSavingName(false);
    }
  }, [username, client, t]);

  const personal: { key: Section; label: string }[] = [
    { key: "account", label: t("settings.section.account") },
    { key: "language", label: t("settings.language") },
  ];
  const workspace: { key: Section; label: string }[] = [
    { key: "billing", label: t("billing.title") },
    { key: "cron", label: t("cron.title") },
    ...(isAdmin ? ([{ key: "admin", label: t("admin.title") }] as { key: Section; label: string }[]) : []),
  ];

  const item = (it: { key: Section; label: string }) => (
    <button
      key={it.key}
      type="button"
      className={`set-item${section === it.key ? " active" : ""}`}
      onClick={() => { setSection(it.key); setEntered(true); }}
    >
      {it.label}
    </button>
  );

  const name = user?.name || user?.email || "—";

  return (
    <div className={`ws-view${entered ? " detail" : ""}`}>
      <aside className="ws-side">
        <div className="ws-side-head">
          <span className="ws-side-headtitle">{t("collab.settings")}</span>
        </div>
        <div className="ws-side-scroll">
          <div className="set-group">{t("settings.group.personal")}</div>
          {personal.map(item)}
          <div className="set-group">{t("settings.group.workspace")}</div>
          {workspace.map(item)}
        </div>
      </aside>

      <section className="ws-content">
        <button type="button" className="ws-mback" onClick={() => setEntered(false)}>‹ {t("collab.settings")}</button>
        <div className="ws-detail-scroll">
          {section === "account" ? (
            <div className="ws-detail">
              <div className="ws-detail-hero">
                <Avatar name={name} id={user?.id} size={56} />
                <div>
                  <h3 className="ws-detail-name">{name}</h3>
                  <p className="ws-detail-status ws-mono">
                    {savedUsername ? `@${savedUsername}` : ""}
                    {savedUsername && user?.email ? " · " : ""}
                    {user?.email ?? ""}
                  </p>
                </div>
              </div>

              <div className="ws-detail-section">
                <div className="ws-detail-label">{t("settings.username")}</div>
                <div className="set-username">
                  <span className="set-username-at ws-mono">@</span>
                  <input
                    className="ws-input set-username-input"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setNameError(null); setNameSaved(false); }}
                    placeholder={t("settings.username.placeholder")}
                    maxLength={30}
                    spellCheck={false}
                    autoCapitalize="none"
                  />
                  <button type="button" className="ws-btn" onClick={() => void saveUsername()} disabled={savingName || username.trim() === (savedUsername ?? "")}>
                    {savingName ? "…" : t("settings.save")}
                  </button>
                </div>
                <p className="ws-hint">{t("settings.username.hint")}</p>
                {nameError ? <p className="ws-warn">{nameError}</p> : null}
                {nameSaved ? <p className="set-ok">{t("settings.username.saved")}</p> : null}
              </div>

              <div className="ws-detail-section">
                <div className="ws-detail-label">{t("settings.session")}</div>
                <button type="button" className="ws-btn danger" onClick={() => void signOut()}>
                  {t("settings.signout")}
                </button>
              </div>
            </div>
          ) : section === "language" ? (
            <div className="ws-detail">
              <div className="ws-detail-label">{t("settings.language")}</div>
              <LangToggle />
            </div>
          ) : section === "billing" ? (
            <div className="settings-embed">
              <BillingModal open onClose={noop} />
            </div>
          ) : section === "cron" ? (
            <div className="settings-embed">
              <CronModal open onClose={noop} />
            </div>
          ) : section === "admin" ? (
            <div className="settings-embed settings-embed-admin">
              <AdminScreen />
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
